import { useMemo, useState } from "react";
import {
  FaChartLine,
  FaBuilding,
  FaFileInvoiceDollar,
  FaCoins,
  FaUndo,
  FaUserTie,
  FaBoxOpen,
  FaTrophy,
  FaChartPie,
} from "react-icons/fa";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import useAuth from "../../../Hooks/useAuth";
import { useGetSalesSummaryQuery } from "../../../store";
import {
  PageHeader,
  FilterTabs,
  EmptyState,
  MascotLoadingOverlay,
} from "../../../Components/shared";
import DashboardSectionCard from "../../../Components/Dashboard/DashboardSectionCard";
import { monthName } from "../../../Components/Dashboard/ConsumptionGraph";
import CustomStyles from "../../../CustomStyles";
import { chartColors } from "../../../Config/Colors";

/**
 * Satış Dashboard'u — YÜKLENEN FATURALARDAN üretilen satış tablosu.
 *
 * KAPSAM: yalnızca satış. Makineden kart/QR ile alınan ödemeler burada DEĞİL,
 * "Ödemeler" sayfasındadır (Pages/payments). Tüm rakamlar Bill kayıtlarından
 * gelir ve iadeler düşülmüştür (net) — bkz. server
 * modules/billing/domain/salesSummary.service.js.
 *
 * Kart dili dashboard sayfasıyla ortaktır (DashboardSectionCard).
 */

const RANGE_FILTERS = [
  { id: "3m", label: "Son 3 Ay" },
  { id: "6m", label: "Son 6 Ay" },
  { id: "12m", label: "Son 1 Yıl" },
];

const RANGE_MONTHS = { "3m": 3, "6m": 6, "12m": 12 };

// Siralama kutulari sabit yukseklikte kalsin diye sayfalanir; server daha
// genis liste doner (topLimit), kutu ayni anda RANK_PAGE_SIZE satir gosterir.
const RANK_PAGE_SIZE = 5;
const RANK_FETCH_LIMIT = 30;

/**
 * Ürün dağılımında gösterilecek tipler. Katalogda başka tipler de olabilir
 * (kampanya ürünü, tek seferlik tanım vb.) ama dağılım grafiği yalnızca bu
 * dört ana ürün için anlamlı — gerisi pastayı kirletiyordu.
 *
 * Eşleşme aksan/büyük-küçük duyarsız: veride "TÜRK KAHVESI" gibi varyantlar
 * geçebiliyor.
 */
const DISTRIBUTION_TYPES = [
  "Çay",
  "Türk Kahvesi",
  "Filtre Kahve",
  "Espresso",
];

const normalizeType = (value) =>
  (value || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g");

const DISTRIBUTION_KEYS = new Set(DISTRIBUTION_TYPES.map(normalizeType));

/** Ürün rengi bulunamayan tipler için dönüşümlü palet. */
const FALLBACK_SLICE_COLORS = [
  CustomStyles.productColors.Americano,
  CustomStyles.productColors.Cappuccino,
  CustomStyles.productColors.Lungo,
  CustomStyles.productColors.Ristretto,
];

const sliceColor = (name, index) =>
  CustomStyles.productColors[name] ||
  FALLBACK_SLICE_COLORS[index % FALLBACK_SLICE_COLORS.length] ||
  chartColors.neutral;

const formatCurrency = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString("tr-TR")} ₺`;

const formatNumber = (value) => (Number(value) || 0).toLocaleString("tr-TR");

/** Eksende ham "1250000" taşıyor; kısaltılmış gösterim. */
const formatCompact = (value) => {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1_000_000) return `${Math.round(v / 100_000) / 10}M`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 100) / 10}B`;
  return String(Math.round(v));
};

/** Aralık filtresinden ISO başlangıç tarihi (ayın ilk günü, gün başı). */
const rangeStart = (rangeId) => {
  const months = RANGE_MONTHS[rangeId] || RANGE_MONTHS["6m"];
  const d = new Date();
  d.setMonth(d.getMonth() - (months - 1));
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// Tailwind JIT sınıf adlarını string birleştirmeyle tarayamaz — `bg-${tone}-50`
// derlenmez. Kombinasyonlar LİTERAL olarak burada durur.
const KPI_TONE = {
  emerald: {
    chip: "bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100",
    icon: "text-emerald-600",
    value: "text-emerald-700",
    bar: "bg-emerald-500",
  },
  rose: {
    chip: "bg-gradient-to-br from-rose-50 to-red-50 border-rose-100",
    icon: "text-rose-600",
    value: "text-rose-700",
    bar: "bg-rose-500",
  },
  indigo: {
    chip: "bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100",
    icon: "text-indigo-600",
    value: "text-indigo-700",
    bar: "bg-indigo-500",
  },
  amber: {
    chip: "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100",
    icon: "text-amber-600",
    value: "text-amber-700",
    bar: "bg-amber-500",
  },
};

/**
 * KPI kutusu — büyük rakam + ikon çipi + opsiyonel oran şeridi.
 * `ratio` 0–1 arası; verilirse kartın altında ince bir dolgu çubuğu çizer.
 */
const KpiCard = ({ icon: Icon, label, value, hint, tone = "indigo", ratio }) => {
  const t = KPI_TONE[tone] || KPI_TONE.indigo;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-Medium text-gray-500">{label}</p>
          <p className={`mt-1 truncate text-2xl font-Bold ${t.value}`}>
            {value}
          </p>
        </div>
        <div className={`shrink-0 rounded-xl border p-2.5 shadow-sm ${t.chip}`}>
          <Icon className={`h-5 w-5 ${t.icon}`} />
        </div>
      </div>
      {hint && <p className="truncate text-[11px] text-gray-400">{hint}</p>}
      {ratio != null && (
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full ${t.bar}`}
            style={{ width: `${Math.min(100, Math.max(0, ratio * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
      <p className="mb-1.5 text-xs font-SemiBold text-gray-800">{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.name}
          className="flex items-center gap-2 text-xs text-gray-600"
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}:{" "}
          <span className="font-SemiBold text-gray-900">
            {entry.name === "Kota"
              ? formatNumber(entry.value)
              : formatCurrency(entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

/** Sıralı liste satırı — oran çubuğu en yüksek değere göre normalize edilir. */
const RankRow = ({ index, title, value, sub, ratio, color }) => (
  <li className="px-3 py-2 sm:px-4">
    <div className="mb-1.5 flex items-baseline justify-between gap-2">
      <span className="flex min-w-0 items-baseline gap-2">
        <span className="w-5 shrink-0 text-[11px] font-SemiBold text-gray-400">
          {index + 1}.
        </span>
        <span className="truncate text-[13px] text-gray-800">{title}</span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[13px] font-SemiBold text-gray-900">
          {value}
        </span>
        {sub && <span className="block text-[10px] text-gray-400">{sub}</span>}
      </span>
    </div>
    <div className="ml-7 h-1.5 overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.max(2, (ratio || 0) * 100)}%`,
          backgroundColor: color,
        }}
      />
    </div>
  </li>
);

/**
 * Sayfalanan sıralama kutusu — kutu yüksekliği liste uzunluğundan bağımsız
 * sabit kalır (boş satırlar yer tutar), böylece yan yana duran iki kart
 * hizasını kaybetmez ve sayfa değişiminde zıplamaz.
 *
 * Sayfalama tasarımı dashboard'daki kartlarla aynı dilde: solda "1-5 / 23"
 * aralığı, sağda Önceki / sayfa çipi / Sonraki (bkz. DeviceStatusDrawer).
 * Ağır `Pagination` atomu bu kadar kısa liste için fazla yer kaplıyordu.
 */
const RankPanel = ({ items, renderRow, emptyText = "Kayıt yok." }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / RANK_PAGE_SIZE));
  // Liste kısalırsa (dönem değişimi) mevcut sayfa taşabilir
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * RANK_PAGE_SIZE;
  const visible = items.slice(start, start + RANK_PAGE_SIZE);
  const filler = RANK_PAGE_SIZE - visible.length;

  if (items.length === 0) {
    return <p className="p-4 text-xs text-gray-400">{emptyText}</p>;
  }

  return (
    <>
      <ol className="divide-y divide-gray-100 py-1">
        {visible.map((item, i) => renderRow(item, start + i))}
        {/* Son sayfa yarım kalınca kart boyu düşmesin */}
        {Array.from({ length: filler }).map((_, i) => (
          <li key={`filler-${i}`} className="h-[52px]" />
        ))}
      </ol>

      {items.length > RANK_PAGE_SIZE && (
        <div className="mt-auto flex items-center justify-between border-t border-gray-100 px-4 py-3">
          <p className="text-xs text-gray-400">
            {`${start + 1}-${Math.min(
              start + RANK_PAGE_SIZE,
              items.length
            )} / ${items.length}`}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-Medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              Önceki
            </button>
            <span className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs font-Medium text-gray-500">
              {safePage}/{totalPages}
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage(safePage + 1)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-Medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Yükleme iskeleti — gerçek yerleşimin (4 KPI + 2/1 grafik satırı + 2 sıralama
 * kutusu) aynısını gri bloklarla çizer, böylece veri gelince yerleşim
 * zıplamaz.
 *
 * Kural: MascotLoadingOverlay HER ZAMAN skeleton'ların üstünde durur
 * (docs/rules/ui-components.md).
 */
const DashboardSkeleton = () => (
  <div className="relative">
    <MascotLoadingOverlay message="Satış verileri yükleniyor..." />

    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`kpi-${i}`}
            className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-full space-y-2">
                <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
                <div className="h-7 w-28 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-gray-100" />
            </div>
            <div className="mt-3 h-1.5 w-full animate-pulse rounded-full bg-gray-100" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm xl:col-span-2">
          <div className="border-b border-gray-200 bg-gray-50 p-3 sm:p-4">
            <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
          </div>
          {/* Sütun yükseklikleri farklı — düz blok yerine grafik hissi */}
          <div className="flex h-[280px] items-end gap-3 p-4">
            {[45, 70, 35, 90, 60, 80].map((h, i) => (
              <div
                key={`bar-${i}`}
                className="flex-1 animate-pulse rounded-t-lg bg-gray-100"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 p-3 sm:p-4">
            <div className="h-5 w-32 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="p-4">
            <div className="mx-auto h-32 w-32 animate-pulse rounded-full bg-gray-100" />
            <div className="mt-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`slice-${i}`}
                  className="h-3 w-full animate-pulse rounded bg-gray-100"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-2">
        {Array.from({ length: 2 }).map((_, card) => (
          <div
            key={`rank-${card}`}
            className="rounded-2xl border border-gray-200 bg-white shadow-sm"
          >
            <div className="border-b border-gray-200 bg-gray-50 p-3 sm:p-4">
              <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
            </div>
            <div className="divide-y divide-gray-100">
              {Array.from({ length: RANK_PAGE_SIZE }).map((_, row) => (
                <div key={`row-${row}`} className="px-3 py-2 sm:px-4">
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <div className="h-3 w-40 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
                  </div>
                  <div className="ml-7 h-1.5 animate-pulse rounded-full bg-gray-100" />
                </div>
              ))}
            </div>
            {/* Sayfalama şeridi */}
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <div className="h-3 w-16 animate-pulse rounded bg-gray-100" />
              <div className="h-7 w-40 animate-pulse rounded-md bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

function SalesDashboardPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const [range, setRange] = useState("6m");

  const { data, isLoading, isFetching } = useGetSalesSummaryQuery({
    token,
    from: rangeStart(range),
    topLimit: RANK_FETCH_LIMIT,
  });

  const summary = data?.data;
  const totals = summary?.totals;
  const busy = isLoading || isFetching;

  const monthly = useMemo(
    () =>
      (summary?.monthly || []).map((m) => ({
        label: `${monthName(m.month)} ${String(m.year).slice(2)}`,
        Ciro: m.netRevenue,
        Kota: m.quota,
      })),
    [summary?.monthly]
  );

  const topFirms = summary?.topFirms || [];
  const topBillers = summary?.topBillers || [];

  // Dağılım yalnızca dört ana ürün tipi için (bkz. DISTRIBUTION_TYPES)
  const productTypes = useMemo(
    () =>
      (summary?.productTypes || []).filter((p) =>
        DISTRIBUTION_KEYS.has(normalizeType(p.productTypeName))
      ),
    [summary?.productTypes]
  );

  const maxFirmRevenue = topFirms[0]?.netRevenue || 0;
  const maxBillerRevenue = topBillers[0]?.netRevenue || 0;
  const productTotal = productTypes.reduce(
    (sum, p) => sum + (p.netRevenue || 0),
    0
  );
  const hasData = (totals?.billCount || 0) > 0;

  // En yüksek aylık ciro — "en iyi ay" KPI'ı için
  const bestMonth = useMemo(
    () =>
      monthly.reduce(
        (best, m) => (!best || m.Ciro > best.Ciro ? m : best),
        null
      ),
    [monthly]
  );

  const headerStats = useMemo(
    () =>
      hasData
        ? [
            {
              icon: FaCoins,
              value: formatCurrency(totals.netRevenue),
              label: "Net Ciro",
              color: "emerald",
            },
            {
              icon: FaFileInvoiceDollar,
              value: formatNumber(totals.billCount),
              label: "Fatura",
              color: "indigo",
            },
          ]
        : [],
    [hasData, totals]
  );

  return (
    // Dashboard sayfasiyla ayni duzen: beyaz PageShell kabugu YOK; sayfa
    // kaydirilabilir bir kart yiginidir. Baslik da kendi kutusundadir.
    <div className="sm:py-2 sm:px-4">
      <div className="flex h-[calc(100vh-64px)] flex-col sm:h-[calc(100vh-16px)]">
        <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 sm:px-0 sm:py-4 sm:pb-8">
          <div className="space-y-3 sm:space-y-4">
            {/* Baslik kutusu — baslik + istatistik cipleri + donem filtresi */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
              <PageHeader
                icon={FaChartLine}
                iconColor="emerald"
                title="Satış"
                subtitle="Yüklenen faturalardan satış analizi"
                stats={headerStats}
                isLoading={busy}
              />
              <div className="mt-3 -mb-3 sm:-mb-4">
                <FilterTabs
                  filters={RANGE_FILTERS}
                  activeFilter={range}
                  onChange={setRange}
                  color="emerald"
                  disabled={isLoading}
                />
              </div>
            </div>

        {/* İlk yükleme: iskelet. Dönem değişiminde (isFetching) mevcut veri
            ekranda kalır, üstüne sadece overlay biner — tablo zıplamasın. */}
        {isFetching && !isLoading && (
          <MascotLoadingOverlay message="Satış verileri yükleniyor..." />
        )}

        {isLoading ? (
          <DashboardSkeleton />
        ) : !hasData ? (
          <EmptyState
            message="Satış verisi bulunamadı"
            suggestion="Seçili dönemde yüklenmiş fatura yok. Farklı bir dönem seçin veya Fatura Yükle sayfasından fatura yükleyin."
          />
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {/* KPI kutuları */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                icon={FaCoins}
                tone="emerald"
                label="Net Ciro"
                value={formatCurrency(totals?.netRevenue)}
                hint={`Brüt ${formatCurrency(totals?.revenue)} · iade düşülmüş`}
                ratio={
                  totals?.revenue
                    ? (totals.netRevenue || 0) / totals.revenue
                    : undefined
                }
              />
              <KpiCard
                icon={FaUndo}
                tone="rose"
                label="İade"
                value={formatCurrency(totals?.returns)}
                hint={`${formatNumber(totals?.returnCount)} iade faturası`}
                ratio={
                  totals?.revenue
                    ? (totals.returns || 0) / totals.revenue
                    : undefined
                }
              />
              <KpiCard
                icon={FaBuilding}
                tone="indigo"
                label="Satış Yapılan Firma"
                value={formatNumber(totals?.firmCount)}
                hint={`${formatNumber(totals?.billCount)} fatura`}
              />
              <KpiCard
                icon={FaBoxOpen}
                tone="amber"
                label="Satılan Kota"
                value={formatNumber(totals?.quota)}
                hint={
                  totals?.freeQuota
                    ? `+${formatNumber(totals.freeQuota)} bedelsiz yükleme`
                    : "Bedelsiz yükleme yok"
                }
              />
            </div>

            {/* Aylık trend + ürün dağılımı */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-3">
              <DashboardSectionCard
                color="emerald"
                icon={<FaChartLine className="h-4 w-4 text-emerald-600" />}
                title="Aylık Satış"
                subtitle={
                  bestMonth
                    ? `En yüksek ay: ${bestMonth.label} — ${formatCurrency(
                        bestMonth.Ciro
                      )}`
                    : "İadeler düşülmüş net ciro"
                }
                className="xl:col-span-2"
              >
                {/* flex-1: kart yüksekliği yan kartla eşitlendiğinde grafik
                    de o yüksekliğe yayılır, altta boşluk kalmaz */}
                <div className="flex min-h-[280px] flex-1 flex-col p-3 sm:p-4">
                  {/* min-h-0 + flex-1: ResponsiveContainer yuzde yukseklik
                      icin cozulmus bir kutu ister */}
                  <div className="min-h-0 flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={monthly}
                      barCategoryGap="35%"
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        stroke={chartColors.grid}
                        strokeOpacity={0.35}
                      />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12, fill: chartColors.axis }}
                      />
                      <YAxis
                        yAxisId="left"
                        tickLine={false}
                        axisLine={false}
                        width={48}
                        tickFormatter={formatCompact}
                        tick={{ fontSize: 12, fill: chartColors.axis }}
                      />
                      {/* Kota ciroyla aynı ölçekte değil — kendi ekseninde */}
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickLine={false}
                        axisLine={false}
                        width={40}
                        tickFormatter={formatCompact}
                        tick={{ fontSize: 11, fill: chartColors.neutral }}
                      />
                      <Tooltip content={<ChartTooltip />} cursor={false} />
                      <Bar
                        yAxisId="left"
                        dataKey="Ciro"
                        fill={CustomStyles.productColors["Çay"]}
                        radius={[6, 6, 0, 0]}
                        barSize={24}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="Kota"
                        stroke={chartColors.bar}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </DashboardSectionCard>

              <DashboardSectionCard
                color="amber"
                icon={<FaChartPie className="h-4 w-4 text-amber-600" />}
                title="Ürün Dağılımı"
                subtitle="Net ciroya göre"
              >
                <div className="flex flex-col p-3 sm:p-4">
                  {productTypes.length === 0 ? (
                    <p className="text-xs text-gray-400">Kayıt yok.</p>
                  ) : (
                    <>
                      <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={productTypes}
                              dataKey="netRevenue"
                              nameKey="productTypeName"
                              innerRadius="58%"
                              outerRadius="88%"
                              paddingAngle={2}
                            >
                              {productTypes.map((entry, index) => (
                                <Cell
                                  key={entry.productTypeName}
                                  fill={sliceColor(
                                    entry.productTypeName,
                                    index
                                  )}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => formatCurrency(value)}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {productTypes.map((p, index) => (
                          <li key={p.productTypeName}>
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{
                                    backgroundColor: sliceColor(
                                      p.productTypeName,
                                      index
                                    ),
                                  }}
                                />
                                <span className="truncate text-[13px] text-gray-700">
                                  {p.productTypeName}
                                </span>
                              </span>
                              <span className="shrink-0 text-[13px] font-SemiBold text-gray-900">
                                {productTotal > 0
                                  ? `%${Math.round(
                                      (p.netRevenue / productTotal) * 100
                                    )}`
                                  : "-"}
                              </span>
                            </div>
                            {/* nokta (10px) + gap (8px) kadar girinti */}
                            <p className="ml-4 pl-0.5 text-[10px] text-gray-400">
                              {formatCurrency(p.netRevenue)} ·{" "}
                              {formatNumber(p.quota)} kota
                            </p>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </DashboardSectionCard>
            </div>

            {/* Firma + fatura kesen sıralamaları */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-2">
              <DashboardSectionCard
                color="indigo"
                icon={<FaTrophy className="h-4 w-4 text-indigo-600" />}
                title="En Çok Satış Yapılan Firmalar"
                subtitle="Net ciroya göre sıralı"
              >
                <RankPanel
                  items={topFirms}
                  renderRow={(firm, index) => (
                    <RankRow
                      key={firm.firmId || firm.firmName}
                      index={index}
                      title={firm.firmName}
                      value={formatCurrency(firm.netRevenue)}
                      sub={`${formatNumber(firm.quota)} kota · ${formatNumber(
                        firm.billCount
                      )} fatura`}
                      ratio={
                        maxFirmRevenue > 0 ? firm.netRevenue / maxFirmRevenue : 0
                      }
                      color={CustomStyles.productColors["Çay"]}
                    />
                  )}
                />
              </DashboardSectionCard>

              <DashboardSectionCard
                color="blue"
                icon={<FaUserTie className="h-4 w-4 text-blue-600" />}
                title="Fatura Kesenler"
                subtitle="Kestiği faturaların net cirosuna göre"
              >
                <RankPanel
                  items={topBillers}
                  renderRow={(biller, index) => (
                    <RankRow
                      key={biller.billerName}
                      index={index}
                      title={biller.billerName}
                      value={formatCurrency(biller.netRevenue)}
                      sub={`${formatNumber(biller.billCount)} fatura`}
                      ratio={
                        maxBillerRevenue > 0
                          ? biller.netRevenue / maxBillerRevenue
                          : 0
                      }
                      color={chartColors.bar}
                    />
                  )}
                />
              </DashboardSectionCard>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesDashboardPage;
